import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Calendar, Loader } from "lucide-react";
import { formatPrice as formatPriceUtil } from "@/utils/currency";
import { toast } from "sonner";
import {
  PaymentMethodSelect,
  PaymentMethod,
} from "@/components/checkout/PaymentMethodSelect";
import { BookingSlot } from "@/templates/TemplateInterface";
import { parseLocalDateKey } from "@/components/booking/BookingDateTimePicker";
import { format } from "date-fns";

export type BookingCheckoutPayload = {
  slot: BookingSlot;
  bookingData: {
    startDate: string;
    endDate: string;
    startTime: string;
    endTime: string;
    quantity: number;
  };
  customerInfo: {
    name: string;
    email: string;
    phone: string;
    address: string;
  };
};

type StorefrontBookingCheckoutProps = {
  payload: BookingCheckoutPayload;
  goBack: () => void;
  storeUrl: string;
  currency?: string;
  whatsappNumber?: string;
  primaryColor?: string;
  paymentOptions?: Record<string, any>;
  whatsappCheckoutEnabled?: boolean;
};

function formatBookingWhen(data: BookingCheckoutPayload["bookingData"]) {
  try {
    const start = format(parseLocalDateKey(data.startDate), "MMM d, yyyy");
    const end = format(parseLocalDateKey(data.endDate), "MMM d, yyyy");
    if (data.startTime) {
      return `${start}${data.startTime ? ` · ${data.startTime}` : ""}${
        data.endTime ? ` – ${data.endTime}` : ""
      }`;
    }
    if (data.startDate !== data.endDate) {
      return `${start} → ${end}`;
    }
    return start;
  } catch {
    return data.startDate;
  }
}

export function StorefrontBookingCheckout({
  payload,
  goBack,
  storeUrl,
  currency = "NGN",
  whatsappNumber,
  primaryColor = "#3b82f6",
  paymentOptions = {},
  whatsappCheckoutEnabled = false,
}: StorefrontBookingCheckoutProps) {
  const navigate = useNavigate();
  const { slot, bookingData, customerInfo } = payload;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | "">("");
  const [paymentDetails, setPaymentDetails] = useState<{
    proofOfPayment?: File;
    notes?: string;
  }>({});
  const [notes, setNotes] = useState("");

  const formatPrice = (price: number) =>
    formatPriceUtil(price, { currency });

  const handlePaymentMethodSelect = (
    method: PaymentMethod,
    details?: { proofOfPayment?: File; notes?: string },
  ) => {
    setPaymentMethod(method);
    if (details) setPaymentDetails(details);
  };

  const handleSubmitBooking = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!bookingData.startDate || !bookingData.endDate) {
      toast.error("Please select booking dates");
      return;
    }

    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
      toast.error("Please fill in all required customer information");
      return;
    }

    if (!paymentMethod) {
      toast.error("Please select a payment method");
      return;
    }

    if (paymentMethod === "bankTransfer" && !paymentDetails.proofOfPayment) {
      toast.error("Please upload proof of payment");
      return;
    }

    try {
      setIsSubmitting(true);

      const formData = new FormData();
      const bookingPayload = {
        customer: customerInfo,
        slotId: slot._id || slot.id,
        bookingDetails: {
          startDate: bookingData.startDate,
          endDate: bookingData.endDate,
          startTime: bookingData.startTime || undefined,
          endTime: bookingData.endTime || undefined,
          quantity: bookingData.quantity || 1,
        },
        payment: {
          method:
            paymentMethod === "bankTransfer"
              ? "bank_transfer"
              : paymentMethod === "cashOnDelivery"
                ? "cash_on_delivery"
                : paymentMethod === "manualPayment"
                  ? "manual_payment"
                  : paymentMethod,
          notes: paymentDetails.notes || "",
        },
        notes,
        subtotal: slot.price,
        total: slot.price,
      };

      formData.append("bookingData", JSON.stringify(bookingPayload));
      if (paymentDetails.proofOfPayment) {
        formData.append("paymentProof", paymentDetails.proofOfPayment);
      }

      const response = await api.post("/api/bookings/public", formData, {
        headers: { "store-url": storeUrl },
      });

      if (response.data?.success && response.data?.booking) {
        const createdBooking = response.data.booking;

        if (paymentMethod === "whatsapp" && whatsappNumber) {
          const bookingId = createdBooking._id;
          const bookingNumber =
            createdBooking.bookingNumber ||
            (typeof bookingId === "string"
              ? bookingId.slice(-8).toUpperCase()
              : "");
          const totalAmount =
            createdBooking.pricing?.total ||
            slot.price * (bookingData.quantity || 1);
          const invoiceUrl = `${window.location.origin}/${storeUrl}/booking-confirmation/${bookingId}`;
          const message = [
            `Booking #${bookingNumber || bookingId}`,
            `Slot: ${slot.name}`,
            `When: ${formatBookingWhen(bookingData)}`,
            "",
            `Total: ${formatPrice(totalAmount)}`,
            "",
            `View booking details: ${invoiceUrl}`,
          ].join("\n");

          const whatsappUrl = `https://wa.me/${whatsappNumber.replace(
            /[^\d]/g,
            "",
          )}?text=${encodeURIComponent(message)}`;
          const newWindow = window.open(whatsappUrl, "_blank");
          if (!newWindow) window.location.href = whatsappUrl;
        }

        toast.success("Booking created successfully!");
        navigate(`/${storeUrl}/booking-confirmation/${createdBooking._id}`);
      }
    } catch (error: any) {
      console.error("Error submitting booking:", error);
      toast.error(
        error.response?.data?.message ||
          "There was an error processing your booking. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pb-6">
      <div className="mb-8 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={goBack}
          className="rounded-full border-black/10 text-black/70 hover:!bg-black/[0.04] hover:!text-black"
        >
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Back
        </Button>
        <div>
          <p
            className="font-heading text-xs font-semibold uppercase tracking-[0.18em]"
            style={{ color: `${primaryColor}99` }}
          >
            Checkout
          </p>
          <h2 className="font-display text-2xl font-black tracking-tight text-black sm:text-3xl">
            Complete your booking
          </h2>
        </div>
      </div>

      <form onSubmit={handleSubmitBooking}>
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[1fr_300px]">
          <div className="space-y-4">
            <div className="rounded-[1.5rem] border border-black/5 bg-white p-5">
              <h3 className="mb-4 font-heading text-sm font-bold">
                Booking summary
              </h3>
              <div className="flex items-start gap-4">
                {slot.images?.[0] ? (
                  <img
                    src={slot.images[0].url}
                    alt={slot.name}
                    className="size-20 shrink-0 rounded-2xl object-cover ring-1 ring-black/5"
                  />
                ) : (
                  <div
                    className="flex size-20 shrink-0 items-center justify-center rounded-2xl ring-1 ring-black/5"
                    style={{ backgroundColor: `${primaryColor}12` }}
                  >
                    <Calendar
                      className="h-8 w-8"
                      style={{ color: `${primaryColor}55` }}
                    />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-black">{slot.name}</p>
                  <p className="mt-1.5 flex items-center gap-1.5 text-sm text-black/50">
                    <Calendar className="h-3.5 w-3.5 shrink-0" />
                    <span>{formatBookingWhen(bookingData)}</span>
                  </p>
                  <p className="mt-1 text-xs text-black/40">
                    {customerInfo.name} · {customerInfo.email} ·{" "}
                    {customerInfo.phone}
                  </p>
                </div>
                <p
                  className="shrink-0 text-base font-bold"
                  style={{ color: primaryColor }}
                >
                  {formatPrice(slot.price)}
                </p>
              </div>
            </div>

            <div className="rounded-[1.5rem] border border-black/5 bg-white p-5">
              <div className="mb-4 flex items-center gap-3">
                <span
                  className="flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                  style={{ backgroundColor: primaryColor }}
                >
                  1
                </span>
                <div>
                  <h3 className="font-heading text-sm font-bold">
                    Payment method
                  </h3>
                  <p className="text-xs text-black/40">
                    Select how you want to pay
                  </p>
                </div>
              </div>
              <PaymentMethodSelect
                onSelectMethod={handlePaymentMethodSelect}
                paymentOptions={paymentOptions}
                showWhatsApp={
                  Boolean(whatsappNumber) && Boolean(whatsappCheckoutEnabled)
                }
              />
            </div>

            <div className="rounded-[1.5rem] border border-black/5 bg-white p-5">
              <div className="mb-4 flex items-center gap-3">
                <span
                  className="flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                  style={{ backgroundColor: primaryColor }}
                >
                  2
                </span>
                <div>
                  <h3 className="font-heading text-sm font-bold">
                    Additional notes
                  </h3>
                  <p className="text-xs text-black/40">
                    Any special requests? (optional)
                  </p>
                </div>
              </div>
              <Textarea
                placeholder="Any special requests or notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="resize-none rounded-2xl border-black/10"
              />
            </div>
          </div>

          <div className="space-y-3 lg:sticky lg:top-24">
            <div className="rounded-[1.5rem] border border-black/5 bg-white p-5">
              <h3 className="mb-4 font-heading text-sm font-bold">
                Order total
              </h3>
              <div className="flex justify-between text-sm text-black/45">
                <span>Subtotal</span>
                <span className="font-medium text-black">
                  {formatPrice(slot.price)}
                </span>
              </div>
              <div className="mt-3 flex justify-between border-t border-black/5 pt-3 text-base font-semibold text-black">
                <span>Total</span>
                <span style={{ color: primaryColor }}>
                  {formatPrice(slot.price)}
                </span>
              </div>
              <Button
                type="submit"
                className="mt-4 h-12 w-full rounded-full font-semibold text-white hover:!text-white hover:opacity-90"
                disabled={isSubmitting}
                style={{ backgroundColor: primaryColor }}
              >
                {isSubmitting ? (
                  <>
                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Complete Booking"
                )}
              </Button>
            </div>
            <p className="px-2 text-center text-xs text-black/35">
              By booking, you agree to our Terms of Service.
            </p>
          </div>
        </div>
      </form>
    </div>
  );
}
